import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {HttpRequestErrorHandler} from '@rbaip/ui-component';
import {Subject} from 'rxjs';
import {AuctionEvent} from '../domain/auction-event/auction-event';
import {EquipmentCategory, EquipmentManufacturer, EquipmentModel, EquipmentType, SellerLocation} from '../domain/item';
import {Opportunity} from '../domain/opportunity/opportunity.model';
import {appConfig} from '../app.config';


@Injectable({
  providedIn: 'root'
})
export class AddAssetService {

  data = {
    siteAuctionEvent: Array<AuctionEvent>(),
    locationList: Array<SellerLocation>(),
    opportunity: Opportunity
  };
  allSitesAuctionEventListCallback: Subject<AuctionEvent[]> = new Subject<AuctionEvent[]>();
  allLocationListCallback: Subject<SellerLocation[]> = new Subject<SellerLocation[]>();
  opportunityCallBack: Subject<Opportunity> = new Subject<Opportunity>();

  private manufacturerList: EquipmentManufacturer[] = [];
  private modelList: EquipmentModel[] = [];
  private categoryList: EquipmentCategory[] = [];
  private typeList: EquipmentType[] = [];

  constructor(private http: HttpClient) {

  }

  public loadAllManufacturer(): Observable<any> {
    const url = '/api/allManufacturer';
    return this.http.get<any>(url,  appConfig.httpOptions);
  }

  public loadModelForSelectedMake(mfrName): Observable<any> {
    const url = '/api/modelsForSelectedMake?mfrName=' + mfrName ;
    return this.http.get<any>(url,  appConfig.httpOptions);
  }

  public getCategoryListByMakeAndModel(modelId, mfrId): Observable<any> {
    const url = '/api/getCategoryListByMakeAndModel?modelName=' + modelId + '&mfrName=' + mfrId + '&localeId=1&realmId=1';
    return this.http.get<any>(url,  appConfig.httpOptions);
  }

  public getCategoryList(manufacturerId: number, modelId: number, typeId: number): Observable<any> {
    return new Observable( (observer) => {
     if ( !manufacturerId && !modelId && !typeId && this.categoryList.length > 0 ) {
       observer.next(this.categoryList);
     } else {
       const url = '/api/equipment-categories?manufacturerId=' + manufacturerId + '&modelId=' + modelId + '&typeId=' + typeId;
       this.http.get<any>(url,  appConfig.httpOptions).subscribe( response => {
         if ( !manufacturerId && !modelId && !typeId && this.categoryList.length === 0 ) {
           this.categoryList = response;
         }
         observer.next(response);
       });
     }
    });
  }

  public getPreLoadedCategoryList(): EquipmentCategory[] {
    return this.categoryList;
  }

  public getTypesList(manufacturerId: number, modelId: number, categoryId: number): Observable<any> {
    return new Observable( ( observer ) => {
      if ( !manufacturerId && !modelId && !categoryId && this.typeList.length > 0 ) {
        observer.next(this.typeList);
      } else {
        const url = '/api/equipment-types?manufacturerId=' + manufacturerId + '&modelId=' + modelId + '&categoryId=' + categoryId;
        this.http.get<any>(url,  appConfig.httpOptions).subscribe( response => {
          if ( !manufacturerId && !modelId && !categoryId && this.typeList.length === 0 ) {
            this.typeList = response;
          }
          observer.next(response);
        });
      }
    });
  }

  public getPreLoadedTypeList(): EquipmentType[] {
    return this.typeList;
  }

  public getManufacturerList(modelId: number, categoryId: number , typeId: number): Observable<any> {
    return new Observable ( (observer) => {
      if ( !modelId && !categoryId && !typeId && this.manufacturerList.length > 0 ) {
        observer.next(this.manufacturerList);
      } else {
        const url = '/api/equipment-manufacturers?modelId=' + modelId + '&categoryId=' + categoryId + '&typeId=' + typeId;
        return this.http.get<any>(url,  appConfig.httpOptions).subscribe( response => {
          if ( !modelId && !categoryId && !typeId && this.manufacturerList.length === 0 ) {
            this.manufacturerList = response;
          }
          observer.next(response);
        });
      }
    });
  }

  public getPreLoadedManufacturerList(): EquipmentManufacturer[] {
    return this.manufacturerList;
  }

  public getModelList(manufacturerId: number, categoryId: number, typeId: number): Observable<any> {
    return new Observable((observer) => {
      if ( !manufacturerId && !categoryId && !typeId && this.modelList.length > 0 ) {
        observer.next(this.modelList);
      } else {
        const url = '/api/equipment-models-manufacturer?manufacturerId=' + manufacturerId + '&categoryId=' + categoryId + '&typeId=' + typeId;
        this.http.get<any>(url,  appConfig.httpOptions).subscribe( response => {
          if ( !manufacturerId && !categoryId && !typeId && this.modelList.length === 0 ) {
            this.modelList = response;
          }
          observer.next(response);
        });
      }
    });
  }

  public getPreLoadedModelList(): EquipmentModel[] {
    return this.modelList;
  }

  public getCategoryById(id: number): Observable<any> {
    const url = '/api/equipment-categories/' + id;
    return this.http.get<any>(url,  appConfig.httpOptions);
  }

  public fetchEquipmentTypesForCategory(catId, modelId, mfrId): Observable<any> {
    const url = '/api/equipmentTypes?catId=' + catId + '&modelName=' + modelId + '&mfrName=' + mfrId;
    return this.http.get<any>(url, appConfig.httpOptions);
  }

  public createNewAsset(assetDto): Observable<any> {
    const url = '/api/createAsset';
    const body = JSON.stringify(assetDto);
    return this.http.post(url, body, appConfig.httpOptions);
  }

  public  loadSites(): Observable<any> {
    const errMsg = 'Error while loading Site: ';
    const defaultQueryString = '';
    const defaultResultLimit = 200;
    const serviceUri = '/api/site/search?query=' + defaultQueryString + '&limit=' + defaultResultLimit;
    return this.http.get<any>(serviceUri,  appConfig.httpOptions);
  }

  getAuctionEventListToGoBack(days: number): void {
    const url =  '/api/auction-events?numberOfDaysToGoBack=' + days;

    this.http.get(url, appConfig.httpOptions)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('getAuctionEventList', []))
      )
      .subscribe(response => {
        this.data.siteAuctionEvent = response as AuctionEvent[];
        this.allSitesAuctionEventListCallback.next(this.data.siteAuctionEvent);
      });
  }

  getLocationListByACG(acgId: number ): void {
    const url = '/api/storage-location?acgId=' + acgId;

    this.http.get(url, appConfig.httpOptions)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('getLocationList', []))
      )
      .subscribe(response => {
        this.data.locationList = response as SellerLocation[];
        this.allLocationListCallback.next(this.data.locationList);
      });
  }

}
